/**
 * Utility functions for subdomain-based company identification
 */

/**
 * Extract subdomain from hostname
 * @param hostname - The hostname (e.g., "company-a.localhost" or "company-a.example.com")
 * @returns The subdomain (e.g., "company-a")
 */
export const extractSubdomain = (hostname: string): string => {
  const parts = hostname.split('.');
  console.log("extractSubdomain - hostname:", hostname, "parts:", parts);
  
  // Handle localhost development
  if (hostname.includes('localhost')) {
    // Remove "www" if it's the first part
    if (parts[0] === 'www') {
      const subdomain = parts[1] || '';
      console.log("localhost with www - subdomain:", subdomain);
      return subdomain;
    }
    const subdomain = parts[0];
    console.log("localhost without www - subdomain:", subdomain);
    return subdomain;
  }
  
  // Handle production domains
  if (parts.length >= 2) {
    // If we have www as the first part, get the second part
    if (parts[0] === 'www') {
      const subdomain = parts[1] || '';
      console.log("production with www - subdomain:", subdomain);
      return subdomain;
    }
    
    // For domains like sentechonline.com, there's no subdomain
    // For domains like company.sentechonline.com, company is the subdomain
    if (parts.length === 2) {
      // This is a main domain (e.g., sentechonline.com), no subdomain
      console.log("main domain - no subdomain");
      return 'sentechonline';
    } else if (parts.length > 2) {
      // This has a subdomain (e.g., company.sentechonline.com)
      const subdomain = parts[0];
      console.log("production with subdomain - subdomain:", subdomain);
      return subdomain;
    }
  }
  
  console.log("fallback - no subdomain");
  return 'sentechonline';
};

/**
 * Get the current subdomain from window.location.hostname
 * @returns The current subdomain
 */
export const getCurrentSubdomain = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  
  return extractSubdomain(window.location.hostname);
};

/**
 * Check if we're in a subdomain environment
 * @returns true if we're using subdomains
 */
export const isSubdomainEnvironment = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  console.log("isSubdomainEnvironment - hostname:", hostname, "parts:", parts);
  
  // Handle localhost development
  if (hostname.includes('localhost')) {
    // If we have www as the first part, check if there's a subdomain after it
    if (parts[0] === 'www' && parts.length > 2) {
      console.log("localhost with www and subdomain - true");
      return true;
    }
    
    // Otherwise, check if we have more than just domain.tld
    const result = parts.length > 2;
    console.log("localhost subdomain check - result:", result);
    return result; // More than just domain.tld (excluding www)
  }
  
  // Handle production domains
  if (parts.length >= 2) {
    // For domains like sentechonline.com or www.sentechonline.com, no subdomain
    if (parts.length === 2) {
      // console.log("production main domain - false");
      return true;
    }
    
    // For domains like company.sentechonline.com, has subdomain
    if (parts.length > 2) {
      console.log("production with subdomain - true");
      return true;
    }
  }
  
  console.log("fallback - false");
  return false;
};

/**
 * Convert company name to subdomain format
 * @param companyName - The company name
 * @returns Subdomain-friendly version of the company name
 */
export const companyNameToSubdomain = (companyName: string): string => {
  return companyName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-') // Replace non-alphanumeric chars with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Convert subdomain to display name
 * @param subdomain - The subdomain
 * @returns Display-friendly version of the subdomain
 */
export const subdomainToDisplayName = (subdomain: string): string => {
  return subdomain
    .replace(/-/g, ' ') // Replace hyphens with spaces
    .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize first letter of each word
}; 